// Icon renderers for offer-page bullet lists.
//
// Extracted from OfferPageBuilderSimple.js.
//   - getListIcon returns a React element (used in editor preview).
//   - getListIconHTML returns an HTML string (used for export).
//
// The two functions support the same set of `icon` keys; keep them in sync
// when adding new icons.

import React from 'react';
import { Box } from '@mui/material';
import {
  Verified as VerifiedIcon,
  Star as StarIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Check as CheckMarkIcon,
  Clear as CloseMarkIcon,
} from '@mui/icons-material';

export function getListIcon(icon, idx) {
  switch (icon) {
    case 'green-number':
      return (
        <Box sx={{
          width: 28, height: 28, borderRadius: '6px', background: '#22c55e',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontWeight: 'bold', fontSize: '1.2rem'
        }}>
          {idx !== undefined ? idx + 1 : ''}
        </Box>
      );
    case 'circle-number':
      return (
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%', border: '2px solid #22c55e',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#22c55e', fontWeight: 'bold', fontSize: '1.2rem', background: 'white'
        }}>
          {idx !== undefined ? idx + 1 : ''}
        </Box>
      );
    case 'check': return <VerifiedIcon sx={{ color: '#b6ff3c', fontSize: '2rem' }} />;
    case 'star': return <StarIcon sx={{ color: '#ffe066', fontSize: '2rem' }} />;
    case 'info': return <InfoIcon sx={{ color: '#40c4ff', fontSize: '2rem' }} />;
    case 'warning': return <WarningIcon sx={{ color: '#ffb347', fontSize: '2rem' }} />;
    case 'red-x': return (
      <Box sx={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        color: '#e53e3e',
        fontWeight: 'bold'
      }}>
        ✗
      </Box>
    );
    case 'pink-check': return (
      <Box sx={{
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: '#ff9a9e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1rem',
        color: 'white',
        fontWeight: 'bold'
      }}>
        ✓
      </Box>
    );
    case 'dark-check': return (
      <Box sx={{
        width: '24px',
        height: '24px',
        borderRadius: '4px',
        backgroundColor: '#2d3748',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1rem',
        color: 'white',
        fontWeight: 'bold'
      }}>
        ✓
      </Box>
    );
    case 'green-badge': return (
      <Box sx={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: '#68d391',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.2rem',
        color: 'white',
        fontWeight: 'bold',
        border: '2px solid #48bb78'
      }}>
        ✓
      </Box>
    );
    case 'star-badge': return (
      <Box sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.2rem',
        color: 'white',
        fontWeight: 'bold',
        border: '2px solid #5a67d8'
      }}>
        ⭐
      </Box>
    );
    case 'green-check-simple': return <CheckMarkIcon sx={{ color: '#22c55e', fontSize: '1.75rem', fontWeight: 'bold' }} />;
    case 'red-x-simple': return <CloseMarkIcon sx={{ color: '#ff3b30', fontSize: '1.75rem', fontWeight: 'bold' }} />;
    default: return <VerifiedIcon sx={{ color: '#b6ff3c', fontSize: '2rem' }} />;
  }
}

// For HTML export (returns SVG or emoji as a string)
export function getListIconHTML(icon) {
  switch (icon) {
    case 'check':
      return '<svg style="color: #b6ff3c; width: 2rem; height: 2rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
    case 'star':
      return '<svg style="color: #ffe066; width: 2rem; height: 2rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>';
    case 'info':
      return '<svg style="color: #40c4ff; width: 2rem; height: 2rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>';
    case 'warning':
      return '<svg style="color: #ffb347; width: 2rem; height: 2rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>';
    case 'red-x':
      return '<span style="color: #e53e3e; font-size: 1.5rem; font-weight: bold;">✗</span>';
    case 'pink-check':
      return '<span style="display: inline-block; width: 24px; height: 24px; border-radius: 50%; background-color: #ff9a9e; color: white; text-align: center; line-height: 24px; font-size: 1rem; font-weight: bold;">✓</span>';
    case 'dark-check':
      return '<span style="display: inline-block; width: 24px; height: 24px; border-radius: 4px; background-color: #2d3748; color: white; text-align: center; line-height: 24px; font-size: 1rem; font-weight: bold;">✓</span>';
    case 'green-badge':
      return '<span style="display: inline-block; width: 32px; height: 32px; border-radius: 50%; background-color: #68d391; color: white; text-align: center; line-height: 32px; font-size: 1.2rem; font-weight: bold; border: 2px solid #48bb78;">✓</span>';
    case 'star-badge':
      return '<span style="display: inline-block; width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; text-align: center; line-height: 32px; font-size: 1.2rem; font-weight: bold; border: 2px solid #5a67d8;">⭐</span>';
    case 'green-check-simple':
      return '<svg style="color: #22c55e; width: 1.75rem; height: 1.75rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>';
    case 'red-x-simple':
      return '<svg style="color: #ff3b30; width: 1.75rem; height: 1.75rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/></svg>';
    default:
      return '<svg style="color: #b6ff3c; width: 2rem; height: 2rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
  }
}
